import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { authAPI, kycAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, Shield, Calendar, CheckCircle, Clock, XCircle, FileText, ShieldCheck, Smartphone, Trash2, Monitor, Camera, Copy, Hash } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [kycForm, setKycForm] = useState({ full_name: user?.name || '', date_of_birth: '', address: '', id_type: 'pan', id_number: '', id_image: null });
  const [kycLoading, setKycLoading] = useState(false);
  const [showKycForm, setShowKycForm] = useState(false);
  const kycFileRef = useRef(null);
  // 2FA
  const [tfaSetup, setTfaSetup] = useState(null);
  const [tfaCode, setTfaCode] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);
  // Device Sessions
  const [sessions, setSessions] = useState([]);
  // Profile Picture
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, or WEBP images'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB'); return;
    }
    setUploading(true);
    try {
      await authAPI.uploadProfilePicture(file);
      toast.success('Profile picture updated');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const copyUserId = () => {
    if (user?.user_id) {
      navigator.clipboard.writeText(user.user_id);
      toast.success('User ID copied!');
    }
  };

  useEffect(() => {
    kycAPI.getStatus().then(({ data }) => setKycStatus(data)).catch(() => {});
    authAPI.getSessions().then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await authAPI.updateProfile({ name, phone });
      toast.success('Profile updated');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setLoading(false); }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    if (!kycForm.full_name || !kycForm.date_of_birth || !kycForm.address || !kycForm.id_number) {
      toast.error('Please fill in all KYC fields'); return;
    }
    if (!kycForm.id_image) {
      toast.error('Please upload your ID document image'); return;
    }
    setKycLoading(true);
    try {
      const { data } = await kycAPI.submit(kycForm);
      setKycStatus(data);
      setShowKycForm(false);
      toast.success('KYC submitted for review');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KYC submission failed');
    } finally { setKycLoading(false); }
  };

  const kycStatusIcon = {
    verified: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    approved: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    pending: <Clock className="w-5 h-5 text-amber-400" />,
    rejected: <XCircle className="w-5 h-5 text-red-400" />,
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="profile-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-green-500/15 flex items-center justify-center">
            <User className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Profile Settings</h1>
            <p className="text-xs text-[var(--text-secondary)]">Manage your account</p>
          </div>
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-6 space-y-5">
          {/* Avatar section */}
          <div className="flex items-center gap-4 pb-5 border-b border-[var(--border-subtle)]">
            <div className="relative group">
              {user?.profile_picture ? (
                <img src={`${BACKEND_URL}${user.profile_picture}`} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-green-500/30" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-green-400">{user?.name?.[0]?.toUpperCase()}</span>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-avatar-btn"
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="w-5 h-5 text-white" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePictureUpload} data-testid="avatar-file-input" />
            </div>
            <div>
              <p className="text-lg font-semibold text-[var(--text-primary)]">{user?.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-xs ${user?.role === 'admin' ? 'bg-green-500/15 text-green-400' : 'bg-zinc-500/15 text-[var(--text-secondary)]'}`}>{user?.role}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${user?.kyc_status === 'verified' ? 'bg-emerald-500/15 text-emerald-400' : user?.kyc_status === 'pending' ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>KYC: {user?.kyc_status}</span>
              </div>
              {user?.user_id && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Hash className="w-3 h-3 text-green-400" />
                  <span className="text-xs font-mono text-green-400 font-semibold" data-testid="profile-user-id">{user.user_id}</span>
                  <button onClick={copyUserId} className="p-0.5 hover:bg-[var(--hover-bg)] rounded transition-colors" data-testid="copy-user-id-btn">
                    <Copy className="w-3 h-3 text-[var(--text-tertiary)] hover:text-green-400" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><User className="w-3 h-3" /> Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email</Label>
              <Input value={user?.email || ''} disabled className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] h-11 cursor-not-allowed" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Joined</Label>
              <Input value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : ''} disabled className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] h-11 cursor-not-allowed" />
            </div>
          </div>

          <button onClick={handleSave} disabled={loading} data-testid="profile-save-btn" className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-40">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* KYC Verification */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-6 space-y-4" data-testid="kyc-section">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-400" /> KYC Verification
            </h3>
            {kycStatus && kycStatusIcon[kycStatus.status]}
          </div>

          {kycStatus?.status === 'not_submitted' || kycStatus?.status === 'rejected' ? (
            <>
              {kycStatus?.status === 'rejected' && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400" data-testid="kyc-rejected-msg">
                  Your previous KYC was rejected. {kycStatus.notes && <span>Reason: {kycStatus.notes}</span>} Please resubmit.
                </div>
              )}
              {!showKycForm ? (
                <div>
                  <p className="text-sm text-[var(--text-secondary)] mb-3">Verify your identity to unlock full trading features.</p>
                  <button onClick={() => setShowKycForm(true)} data-testid="start-kyc-btn"
                    className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm font-medium transition-all">
                    Start KYC Verification
                  </button>
                </div>
              ) : (
                <form onSubmit={handleKycSubmit} className="space-y-3" data-testid="kyc-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">Full Legal Name</Label>
                      <Input value={kycForm.full_name} onChange={(e) => setKycForm({ ...kycForm, full_name: e.target.value })} data-testid="kyc-full-name"
                        className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" placeholder="As on PAN card" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">Date of Birth</Label>
                      <Input type="date" value={kycForm.date_of_birth} onChange={(e) => setKycForm({ ...kycForm, date_of_birth: e.target.value })} data-testid="kyc-dob"
                        className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">ID Type</Label>
                      <select value={kycForm.id_type} onChange={(e) => setKycForm({ ...kycForm, id_type: e.target.value })} data-testid="kyc-id-type"
                        className="w-full h-10 bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] rounded px-3 text-sm">
                        <option value="pan">PAN Card</option>
                        <option value="aadhaar">Aadhaar Card</option>
                        <option value="driving_license">Driving License</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">{kycForm.id_type === 'pan' ? 'PAN Number' : kycForm.id_type === 'aadhaar' ? 'Aadhaar Number' : 'DL Number'}</Label>
                      <Input value={kycForm.id_number} onChange={(e) => setKycForm({ ...kycForm, id_number: kycForm.id_type === 'pan' ? e.target.value.toUpperCase() : e.target.value })} data-testid="kyc-id-number"
                        className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-10"
                        placeholder={kycForm.id_type === 'pan' ? 'ABCDE1234F' : kycForm.id_type === 'aadhaar' ? '1234 5678 9012' : 'DL-1234567890'}
                        maxLength={kycForm.id_type === 'pan' ? 10 : kycForm.id_type === 'aadhaar' ? 14 : 20} />
                    </div>
                    <div className="col-span-1 sm:col-span-2 space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">Address</Label>
                      <Input value={kycForm.address} onChange={(e) => setKycForm({ ...kycForm, address: e.target.value })} data-testid="kyc-address"
                        className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" placeholder="Full residential address" />
                    </div>
                    <div className="col-span-1 sm:col-span-2 space-y-1.5">
                      <Label className="text-xs text-[var(--text-secondary)]">Upload {kycForm.id_type === 'pan' ? 'PAN Card' : kycForm.id_type === 'aadhaar' ? 'Aadhaar Card' : 'Driving License'} Image</Label>
                      <input type="file" accept="image/jpeg,image/png,image/webp" ref={kycFileRef}
                        onChange={(e) => setKycForm({ ...kycForm, id_image: e.target.files?.[0] || null })}
                        data-testid="kyc-image-upload"
                        className="hidden" />
                      <div
                        onClick={() => kycFileRef.current?.click()}
                        className="cursor-pointer border-2 border-dashed border-[var(--border-subtle)] hover:border-green-500/40 rounded-lg p-4 text-center transition-colors"
                        data-testid="kyc-image-dropzone"
                      >
                        {kycForm.id_image ? (
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm text-emerald-400 font-medium">{kycForm.id_image.name}</span>
                            <span className="text-[10px] text-[var(--text-tertiary)]">({(kycForm.id_image.size / 1024).toFixed(0)} KB)</span>
                          </div>
                        ) : (
                          <div>
                            <Camera className="w-6 h-6 text-[var(--text-tertiary)] mx-auto mb-1" />
                            <p className="text-xs text-[var(--text-secondary)]">Click to upload PAN card image</p>
                            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">JPG, PNG or WEBP (max 5MB)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setShowKycForm(false)} className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">Cancel</button>
                    <button type="submit" disabled={kycLoading} data-testid="kyc-submit-btn"
                      className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
                      {kycLoading ? 'Submitting...' : 'Submit KYC'}
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : kycStatus?.status === 'pending' ? (
            <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded" data-testid="kyc-pending-msg">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <p className="text-sm text-amber-400">Your KYC is under review. This typically takes 24-48 hours.</p>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded" data-testid="kyc-verified-msg">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-400">Your identity has been verified. All trading features are unlocked.</p>
            </div>
          )}
        </div>

        {/* 2FA Section */}
        {/* <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-6 space-y-4" data-testid="2fa-section">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-400" /> Two-Factor Authentication
            </h3>
            {user?.two_factor_enabled && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded">Enabled</span>}
          </div>

          {!user?.two_factor_enabled ? (
            <>
              {!tfaSetup ? (
                <div>
                  <p className="text-sm text-[var(--text-secondary)] mb-3">Protect your account with an authenticator app (Google Authenticator, Authy).</p>
                  <button onClick={async () => {
                    setTfaLoading(true);
                    try { const { data } = await authAPI.setup2fa(); setTfaSetup(data); }
                    catch { toast.error('Failed to setup 2FA'); }
                    setTfaLoading(false);
                  }} disabled={tfaLoading} data-testid="setup-2fa-btn"
                    className="px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
                    {tfaLoading ? 'Setting up...' : 'Setup 2FA'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-[var(--text-secondary)]">Scan this QR code with your authenticator app:</p>
                  <div className="flex justify-center">
                    <img src={tfaSetup.qr_code} alt="2FA QR Code" className="w-48 h-48 rounded-lg" data-testid="2fa-qr-code" />
                  </div>
                  <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                    <p className="text-[10px] text-[var(--text-secondary)] mb-1">Manual entry key:</p>
                    <p className="font-mono text-xs text-[var(--text-primary)] break-all" data-testid="2fa-secret">{tfaSetup.secret}</p>
                  </div>
                  <div className="flex gap-2">
                    <Input value={tfaCode} onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, ''))}
                      maxLength={6} placeholder="Enter 6-digit code" data-testid="2fa-verify-input"
                      className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono text-center tracking-widest h-10" />
                    <button onClick={async () => {
                      if (tfaCode.length !== 6) { toast.error('Enter 6 digits'); return; }
                      setTfaLoading(true);
                      try { await authAPI.enable2fa({ code: tfaCode }); toast.success('2FA enabled!'); refreshUser(); setTfaSetup(null); setTfaCode(''); }
                      catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
                      setTfaLoading(false);
                    }} disabled={tfaLoading} data-testid="enable-2fa-btn"
                      className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium whitespace-nowrap disabled:opacity-50">
                      Enable
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <p className="text-sm text-emerald-400">2FA is active. Your account is protected.</p>
              </div>
            </div>
          )}
        </div> */}

        {/* Device Sessions */}
        {/* <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-6 space-y-4" data-testid="sessions-section">
          <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[var(--text-secondary)]" /> Active Sessions
          </h3>
          <div className="space-y-2">
            {sessions.length === 0 ? (
              <p className="text-sm text-[var(--text-secondary)]">No active sessions found.</p>
            ) : sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-4 h-4 text-[var(--text-secondary)]" />
                  <div>
                    <p className="text-xs text-[var(--text-primary)] truncate max-w-[200px]">{s.user_agent || 'Unknown Device'}</p>
                    <p className="text-[10px] text-[var(--text-tertiary)]">IP: {s.ip_masked} &middot; {new Date(s.last_active).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
                <button onClick={async () => {
                  try { await authAPI.revokeSession(s.id); setSessions(prev => prev.filter(x => x.id !== s.id)); toast.success('Session revoked'); }
                  catch { toast.error('Failed'); }
                }} data-testid={`revoke-session-${s.id}`}
                  className="p-1.5 rounded hover:bg-red-500/10 text-[var(--text-tertiary)] hover:text-red-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div> */}

        {/* Account Info */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-6 space-y-3">
          <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2"><FileText className="w-4 h-4 text-[var(--text-secondary)]" /> Account</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
              <span className="text-xs text-[var(--text-secondary)]">Account Status</span>
              <p className={`font-medium mt-1 ${user?.status === 'active' ? 'text-emerald-400' : 'text-red-400'}`}>{user?.status}</p>
            </div>
            <div className="p-3 bg-[var(--bg-primary)] rounded border border-green-500/20">
              <span className="text-xs text-[var(--text-secondary)]">User ID</span>
              <p className="font-mono text-lg font-bold text-green-400 mt-1" data-testid="profile-user-id-card">{user?.user_id || 'N/A'}</p>
              <p className="text-[10px] text-[var(--text-tertiary)]">Use this to login</p>
            </div>
            <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
              <span className="text-xs text-[var(--text-secondary)]">Internal ID</span>
              <p className="font-mono text-xs text-[var(--text-secondary)] mt-1 truncate">{user?.id}</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
